import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { productService } from '../../../services/productService';

const CSVImportModal = ({ onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        const csvFile = acceptedFiles?.[0];
        setFile(csvFile);
        parseCSV(csvFile);
      }
    }
  });

  const parseCSV = (csvFile) => {
    Papa?.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        console.log('Parsed CSV:', results?.data);
        setParsedData(results?.data);
        validateData(results?.data);
      },
      error: (error) => {
        console.error('CSV Parse Error:', error);
        alert('Erro ao processar arquivo CSV');
      }
    });
  };

  const validateData = (data) => {
    const errors = [];
    
    data?.forEach((row, index) => {
      const rowErrors = [];
      
      if (!row?.SKU) rowErrors?.push('SKU obrigatório');
      if (!row?.['Nome do Produto']) rowErrors?.push('Nome obrigatório');
      if (!row?.Marca) rowErrors?.push('Marca obrigatória');
      if (!row?.Categoria) rowErrors?.push('Categoria obrigatória');
      if (!row?.Preco || parseFloat(row?.Preco) <= 0) rowErrors?.push('Preço inválido');
      if (!row?.['Peso (kg)'] || parseFloat(row?.['Peso (kg)']) <= 0) rowErrors?.push('Peso inválido');
      if (!row?.NCM || row?.NCM?.length !== 8) rowErrors?.push('NCM deve ter 8 dígitos');
      
      if (rowErrors?.length > 0) {
        errors?.push({
          row: index + 1,
          errors: rowErrors
        });
      }
    });
    
    setValidationErrors(errors);
  };

  const handleImport = async () => {
    if (!parsedData || parsedData?.length === 0) {
      alert('Nenhum dado para importar');
      return;
    }

    if (validationErrors?.length > 0) {
      const proceed = window.confirm(
        `Existem ${validationErrors?.length} linhas com erros. Deseja importar apenas as linhas válidas?`
      );
      if (!proceed) return;
    }

    setImporting(true);

    try {
      const productsToImport = parsedData?.map(row => ({
        sku: row?.SKU,
        name: row?.['Nome do Produto'],
        brand: row?.Marca,
        category: row?.Categoria,
        supplier: row?.Fornecedor,
        supplierState: row?.['Estado do Fornecedor'],
        price: parseFloat(row?.Preco) || 0,
        cost: parseFloat(row?.Custo) || 0,
        ean: row?.EAN,
        ncm: row?.NCM,
        cest: row?.CEST,
        weight: parseFloat(row?.['Peso (kg)']) || 0,
        width: parseFloat(row?.['Largura (cm)']) || 0,
        height: parseFloat(row?.['Altura (cm)']) || 0,
        length: parseFloat(row?.['Comprimento (cm)']) || 0,
        boxWeight: parseFloat(row?.['Peso da Caixa (kg)']) || 0,
        images: row?.['Imagem Principal'] ? [row?.['Imagem Principal']] : [],
        stock: parseInt(row?.Estoque) || 0,
        condition: 'novo',
        productType: 'product',
        status: 'active'
      }));

      // Import products in batches
      const batchSize = 50;
      for (let i = 0; i < productsToImport?.length; i += batchSize) {
        const batch = productsToImport?.slice(i, i + batchSize);
        await Promise.all(batch?.map(product => productService?.create(product)));
      }

      onImport(productsToImport);
    } catch (err) {
      console.error('Import error:', err);
      alert('Erro ao importar produtos: ' + err?.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[190] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[200] overflow-hidden flex items-center justify-center p-4">
        <div className="bg-background rounded-lg shadow-brand-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Importar CSV</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Importe múltiplos produtos de uma vez
              </p>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Dropzone */}
            {!file && (
              <div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-smooth
                  ${isDragActive 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary hover:bg-muted/50'
                  }
                `}
              >
                <input {...getInputProps()} />
                <Icon name="FileText" size={60} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium text-foreground mb-2">
                  {isDragActive ? 'Solte o arquivo aqui' : 'Arraste um arquivo CSV ou clique para selecionar'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Formato aceito: .csv
                </p>
              </div>
            )}

            {/* File Info */}
            {file && (
              <div className="bg-muted/50 border border-border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon name="FileText" size={24} className="text-primary" />
                  <div>
                    <p className="font-medium text-foreground">{file?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file?.size / 1024)?.toFixed(2)} KB
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => {
                    setFile(null);
                    setParsedData(null);
                    setValidationErrors([]);
                  }}
                />
              </div>
            )}

            {/* Preview */}
            {parsedData && (
              <div>
                <h3 className="font-semibold text-foreground mb-3">
                  Preview ({parsedData?.length} produtos)
                </h3>
                
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-60">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-foreground">SKU</th>
                          <th className="px-4 py-2 text-left font-medium text-foreground">Nome</th>
                          <th className="px-4 py-2 text-left font-medium text-foreground">Marca</th>
                          <th className="px-4 py-2 text-left font-medium text-foreground">Preço</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData?.slice(0, 5)?.map((row, index) => (
                          <tr key={index} className="border-b border-border">
                            <td className="px-4 py-2 text-foreground">{row?.SKU}</td>
                            <td className="px-4 py-2 text-foreground">{row?.['Nome do Produto']}</td>
                            <td className="px-4 py-2 text-foreground">{row?.Marca}</td>
                            <td className="px-4 py-2 text-foreground">R$ {row?.Preco}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {parsedData?.length > 5 && (
                    <div className="px-4 py-2 bg-muted/30 text-xs text-muted-foreground text-center">
                      + {parsedData?.length - 5} produtos adicionais
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {validationErrors?.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-2">
                      {validationErrors?.length} linha(s) com erros
                    </p>
                    <div className="space-y-1 text-sm text-muted-foreground max-h-32 overflow-y-auto">
                      {validationErrors?.slice(0, 5)?.map((error, index) => (
                        <p key={index}>
                          Linha {error?.row}: {error?.errors?.join(', ')}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CSV Format Info */}
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-2">Formato do CSV</h4>
              <p className="text-xs text-muted-foreground mb-2">
                O arquivo deve conter as seguintes colunas:
              </p>
              <div className="text-xs text-muted-foreground font-mono bg-card p-2 rounded">
                SKU, Nome do Produto, Marca, Categoria, Fornecedor, Estado do Fornecedor, Preco, Custo, EAN, NCM, CEST, Peso (kg), Largura (cm), Altura (cm), Comprimento (cm), Peso da Caixa (kg), Imagem Principal, Estoque
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            
            <Button
              iconName="Upload"
              onClick={handleImport}
              disabled={!parsedData || importing}
              loading={importing}
            >
              {importing ? 'Importando...' : `Importar ${parsedData?.length || 0} Produtos`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CSVImportModal;